// Ops API client — uses mock data when running locally

import { getConfig } from './config';
import { mockOverview, mockTeamDetail, mockPlatformHealth } from './mock-data';
import type {
  OverviewResponse,
  TeamDetailResponse,
  PlatformHealthResponse,
} from './types';

class OpsApiClient {
  private apiKey: string = '';

  setApiKey(key: string) {
    this.apiKey = key;
  }

  private async fetch<T>(path: string): Promise<T> {
    const config = getConfig();
    const url = `${config.opsApiUrl}${path}`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || `API error: ${response.status}`);
    }

    return response.json();
  }

  async getOverview(): Promise<OverviewResponse> {
    if (getConfig().isMock) {
      await fakeLag();
      return structuredClone(mockOverview);
    }
    return this.fetch('/api/overview');
  }

  async getTeamDetail(teamId: string): Promise<TeamDetailResponse> {
    if (getConfig().isMock) {
      await fakeLag();
      const detail = mockTeamDetail[teamId];
      if (!detail) throw new Error(`Team '${teamId}' not found`);
      return structuredClone(detail);
    }
    return this.fetch(`/api/teams/${encodeURIComponent(teamId)}`);
  }

  async getPlatformHealth(): Promise<PlatformHealthResponse> {
    if (getConfig().isMock) {
      await fakeLag();
      return structuredClone(mockPlatformHealth);
    }
    return this.fetch('/api/platform/health');
  }
}

/** Simulate network latency so loading states are visible */
function fakeLag(): Promise<void> {
  return new Promise((r) => setTimeout(r, 300 + Math.random() * 400));
}

export const opsApi = new OpsApiClient();
